import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listQualityRules, upsertQualityRule, deleteQualityRule, runQualityIntelligenceSweep,
} from "@/lib/quality-intelligence.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Play, Trash2 } from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/quality-intelligence")({
  component: QualityIntelligencePage,
});

const RULE_LABELS: Record<string, { label: string; recommends: string }> = {
  sku_repeat_failures: {
    label: "SKU repeat failures",
    recommends: "Recommend quarantining the SKU until quality review completes.",
  },
  carrier_damage_pattern: {
    label: "Carrier damage pattern",
    recommends: "Recommend packaging and carrier review for the (SKU, carrier) pair.",
  },
  reason_pattern: {
    label: "Inspection reason pattern",
    recommends: "Recommend QA investigation for any tenant-configured reason.",
  },
  lot_pattern: {
    label: "Lot pattern",
    recommends: "Recommend quarantining remaining lot inventory and opening a quality review.",
  },
};

function QualityIntelligencePage() {
  const qc = useQueryClient();
  const { tenantId } = useScope();
  const listFn = useServerFn(listQualityRules);
  const upsertFn = useServerFn(upsertQualityRule);
  const deleteFn = useServerFn(deleteQualityRule);
  const runFn = useServerFn(runQualityIntelligenceSweep);

  const { data, isLoading } = useQuery({
    queryKey: ["quality-rules", tenantId],
    enabled: !!tenantId,
    queryFn: () => listFn({ data: { tenantId: tenantId! } }),
  });

  const update = useMutation({
    mutationFn: (v: any) => upsertFn({ data: v }),
    onSuccess: () => { toast.success("Rule saved"); qc.invalidateQueries({ queryKey: ["quality-rules", tenantId] }); },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  const del = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => { toast.success("Rule removed"); qc.invalidateQueries({ queryKey: ["quality-rules", tenantId] }); },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  const runSweep = useMutation({
    mutationFn: () => runFn({ data: { tenantId: tenantId! } }),
    onSuccess: (r: any) => {
      const total = Object.values(r?.emitted ?? {}).reduce((a: number, b: any) => a + (b as number), 0);
      toast.success(`Sweep complete — ${total} recommendation(s) emitted`);
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  return (
    <>
      <PageHeader
        title="Quality Intelligence"
        description="Pattern detection that emits recommendations into Operations Workspace. Recommendations are advisory — operators decide."
        actions={
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" asChild>
              <Link to="/app/returns"><ArrowLeft className="h-4 w-4 mr-1" />Back to Returns</Link>
            </Button>
            <Button size="sm" disabled={!tenantId || runSweep.isPending} onClick={() => runSweep.mutate()}>
              <Play className="h-4 w-4 mr-1" />Run sweep now
            </Button>
          </div>
        }
      />
      <div className="p-4 sm:p-6 space-y-4">
        <Card>
          <CardHeader><CardTitle className="text-sm">How This Works</CardTitle></CardHeader>
          <CardContent className="text-sm text-muted-foreground space-y-2">
            <p>
              Each rule defines a threshold over a rolling window of inspected RMA lines.
              When the threshold is crossed, a recommendation appears in Operations Workspace
              with a PolicyExplanation citing this rule. Inventory and SKU policy are never
              mutated automatically — operators act through the standard signal lifecycle.
            </p>
            <p>
              <span className="font-medium text-foreground">Lot pattern</span> stays inert
              until lot tracking is populated on receiving (rma_lines.lot_id). All other
              rules apply to any inspected return.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-sm">Rules</CardTitle></CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-sm text-muted-foreground">Loading…</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Rule</TableHead>
                    <TableHead className="w-24 text-right">Window (d)</TableHead>
                    <TableHead className="w-24 text-right">Threshold</TableHead>
                    <TableHead>Reason codes</TableHead>
                    <TableHead className="w-28">Severity</TableHead>
                    <TableHead className="w-20">Active</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data?.rules ?? []).map((r: any) => (
                    <RuleRow key={r.id} rule={r} onSave={(v) => update.mutate({ ...v, id: r.id, tenantId })} onDelete={() => del.mutate(r.id)} />
                  ))}
                  {(data?.rules ?? []).length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center py-6 text-muted-foreground">No rules. Defaults are seeded per tenant.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function RuleRow({ rule, onSave, onDelete }: { rule: any; onSave: (v: any) => void; onDelete: () => void }) {
  const meta = RULE_LABELS[rule.rule_type] ?? { label: rule.rule_type, recommends: "" };
  const [windowDays, setWindowDays] = useState(rule.window_days);
  const [threshold, setThreshold] = useState(rule.threshold_count);
  const [reasons, setReasons] = useState((rule.reason_codes ?? []).join(", "));
  const [severity, setSeverity] = useState(rule.severity);
  const [active, setActive] = useState(rule.active);
  const save = () => onSave({
    ruleType: rule.rule_type,
    windowDays: Number(windowDays),
    thresholdCount: Number(threshold),
    reasonCodes: rule.rule_type === "reason_pattern"
      ? reasons.split(",").map((s: string) => s.trim()).filter(Boolean)
      : null,
    severity,
    active,
    notes: rule.notes ?? null,
  });
  const dirty =
    Number(windowDays) !== rule.window_days ||
    Number(threshold) !== rule.threshold_count ||
    severity !== rule.severity ||
    active !== rule.active ||
    (rule.rule_type === "reason_pattern" && reasons !== (rule.reason_codes ?? []).join(", "));
  return (
    <TableRow>
      <TableCell className="align-top">
        <div className="font-medium">{meta.label}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{meta.recommends}</div>
        {rule.rule_type === "lot_pattern" && (
          <Badge variant="outline" className="mt-1 text-[10px]">Inert until lot tracking populated</Badge>
        )}
      </TableCell>
      <TableCell className="text-right">
        <Input className="h-8 text-right" type="number" min={1} max={365} value={windowDays} onChange={(e) => setWindowDays(e.target.value)} />
      </TableCell>
      <TableCell className="text-right">
        <Input className="h-8 text-right" type="number" min={1} max={10000} value={threshold} onChange={(e) => setThreshold(e.target.value)} />
      </TableCell>
      <TableCell>
        {rule.rule_type === "reason_pattern" ? (
          <Input className="h-8" value={reasons} onChange={(e) => setReasons(e.target.value)} placeholder="MISSING_PARTS, CARRIER_DAMAGE, …" />
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell>
        <Select value={severity} onValueChange={setSeverity}>
          <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="info">info</SelectItem>
            <SelectItem value="warn">warn</SelectItem>
            <SelectItem value="error">error</SelectItem>
            <SelectItem value="critical">critical</SelectItem>
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell>
        <Switch checked={active} onCheckedChange={setActive} />
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1">
          <Button size="sm" variant="outline" disabled={!dirty} onClick={save}>Save</Button>
          <Button size="icon" variant="ghost" onClick={onDelete} title="Delete rule"><Trash2 className="h-4 w-4" /></Button>
        </div>
      </TableCell>
    </TableRow>
  );
}